<template>
  <div>
    <p class="tw:text-base tw:leading-[20px] tw:mb-6" data-testid="rc-org-property-eligible-text">
      {{ $t('rc_org_property_eligible') }}
    </p>
    <div class="tw:relative tw:w-full tw:md:max-w-[530px]" v-click-outside="clickAway">
      <div
        :class="getClasses"
        class="tw:lg:max-w-[530px] tw:flex tw:items-center tw:justify-between tw:bg-white tw:rounded-lg tw:p-2"
      >
        <button
          class="tw:py-1 tw:px-2 tw:w-full tw:flex tw:items-center"
          data-testid="show-properties-button"
          @click="showProperties"
        >
          <div class="tw:flex tw:flex-col tw:w-full tw:mr-2 tw:text-left">
            <p class="tw:text-gray-600 tw:text-xs">{{ $t('rc_menu_property_header') }}</p>
            <p data-testid="selected-property-name-text" class="tw:text-sm tw:font-bold">
              {{ selectedProperty === null ? $t('rc_menu_property_prompt') : selectedProperty?.name }}
            </p>
          </div>
          <ChevronRight
            v-if="properties.length > 0"
            class="tw:hover:cursor-pointer tw:size-6"
            :class="isOpen && properties.length > 0 && screenWidth > 767 ? 'tw:rotate-270' : 'tw:rotate-90'"
          />
        </button>
        <div class="tw:flex tw:pt-2 tw:lg:pt-0 tw:w-full tw:lg:w-auto">
          <MainButton
            @click="applyPropertySwitch"
            class="tw:w-full tw:lg:w-auto"
            :disabled="selectedProperty === null"
            dataTestId="select-and-switch-to-eligible-item-button"
          >
            <span class="tw:text-nowrap">{{ $t('rc_eligibility_property_select') }}</span>
          </MainButton>
        </div>
      </div>
      <div
        v-show="isOpen && properties.length > 0 && screenWidth > 767"
        :class="`tw:h-[calc(48px_*_${properties.length}_+_24px)]`"
        class="tw:absolute tw:left-0 tw:bg-white shadow tw:min-h-[calc(48px_+_24px)] tw:overflow-scroll tw:w-full tw:top-[70px] tw:rounded-lg"
      >
        <ul class="tw:my-3">
          <li v-for="(property, idx) in properties" :key="property.accommodationId">
            <button
              @click="selectProperty(property)"
              class="tw:w-full tw:py-3 tw:px-[22px] tw:hover:bg-gray-200 tw:flex tw:items-center tw:gap-x-3"
              :class="
                property.accommodationId === selectedProperty?.accommodationId
                  ? 'tw:bg-gray-200 tw:hover:cursor-default'
                  : 'tw:hover:cursor-pointer'
              "
              :data-testid="`property-list-property-${idx + 1}-button`"
            >
              <Organization class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
              <span class="tw:font-bold">{{ property.name }}</span>
            </button>
          </li>
        </ul>
      </div>

      <div
        v-if="isOpen && properties.length > 0 && screenWidth < 1024"
        class="tw:fixed tw:inset-0 tw:top-0 tw:z-51 tw:w-full tw:h-full tw:overflow-hidden tw:flex tw:overflow-y-auto tw:z-100"
      >
        <div class="tw:mx-auto tw:w-full tw:max-w-[980px] tw:bg-white tw:m-auto box-shadow tw:relative">
          <div
            class="tw:fixed tw:z-50 tw:w-full tw:h-[56px] tw:bg-white tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-4 tw:px-5 tw:text-gray-800 shadow"
          >
            <div>
              <button class="tw:outline-hidden tw:pr-2 tw:pt-2" @click="clickAway" @keydown.enter="clickAway">
                <Close class="tw:size-2.5" />
              </button>
            </div>
            <div class="tw:font-bold">{{ $t('rc_opportunities_commission_cta') }}</div>
            <div />
          </div>

          <div class="tw:flex tw:flex-col tw:pb-[76px] tw:pt-[72px] tw:min-h-screen tw:p-5">
            <ul class="tw:my-3">
              <li v-for="property in properties" :key="property.accommodationId">
                <button
                  @click="selectProperty(property)"
                  class="tw:w-full tw:py-3 tw:px-[22px] tw:hover:bg-gray-200 tw:flex tw:items-center tw:gap-x-3"
                  :class="
                    property.accommodationId === selectedProperty.accommodationId
                      ? 'tw:bg-gray-200 tw:hover:cursor-default'
                      : 'tw:hover:cursor-pointer'
                  "
                >
                  <Organization class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
                  <span class="tw:font-bold">{{ property.name }}</span>
                </button>
              </li>
            </ul>
          </div>

          <div
            class="footer shadow tw:fixed tw:bottom-0 tw:w-full tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-4 tw:px-8 tw:text-gray-800 tw:bg-white"
          >
            <div class="tw:md:flex tw:lg:hidden tw:w-full">
              <MainButton class="tw:w-full" @click="applyPropertySwitch" :disabled="selectedProperty === null">
                <span class="tw:text-nowrap">{{ $t('rc_eligibility_property_select') }}</span>
              </MainButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import MainButton from '@/components/base/buttons/MainButton.vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import Close from '@/components/icons/Close.svg?component'
import Organization from '@/components/icons/Organization.svg?component'

import { useIsMobile } from '@/composables/useIsMobile'
import useAccommodations from '@/composables/useAccommodations'
import { storageSession } from '@/utils/storageService'

const store = useStore()
const isOpen = ref(false)
const props = defineProps({
  properties: Array,
})

const selectedOrganisation = computed(() => store.state?.session?.selectedOrganisation)
const { switchAccommodation } = useAccommodations()
const { screenWidth } = useIsMobile()
const selectedProperty = ref(null)
const selectProperty = property => {
  selectedProperty.value = property
  if (screenWidth.value > 1024) {
    isOpen.value = false
  }
}

const showProperties = () => {
  isOpen.value = !isOpen.value
}
const clickAway = () => {
  isOpen.value = false
}

const applyPropertySwitch = async () => {
  await switchAccommodation(selectedProperty.value, selectedOrganisation.value)
  storageSession.removeItem('new_campaign_created')
}

const getClasses = computed(() => {
  const classes = []

  if (props.properties.length > 1) {
    classes.push('tw:hover:cursor-pointer')
  }
  classes.push('shadow tw:mr-0 tw:md:mr-6 tw:lg:mr-0 tw:flex-col tw:lg:flex-row tw:lg:max-w-[590px] tw:mb-12')

  return classes.join(' ')
})
</script>
